/**
 * src/data/loaders.ts — load the SOFR history at runtime (§7, §10.9).
 *
 * Trust boundary 1 (§8.2): the fetched file is validated with Zod. Invalid SOFR
 * rows (non-numeric, "." for non-business days, bad dates) are DROPPED, not allowed
 * to become NaN. A structurally invalid file falls back to the bundled copy, then to
 * a clear warning. The browser NEVER calls FRED directly — it only reads the committed
 * file produced by the scheduled GitLab CI job.
 *
 * SOFR is the default prefill for the base rate, not a constraint: the rate is a plain
 * user input, so a line priced off Prime or a house base rate is modeled by editing it.
 */
import { sofrObservationSchema } from '@/schema';
import type { SofrObservation } from '@/types';
import fallbackSofr from './fallbackSofr.json';

export type DataSource = 'remote' | 'bundled-fallback';

export interface SofrLoadResult {
  observations: SofrObservation[];
  /** Latest (most recent date) rate — used to seed default scenarios (§8.1). */
  latestRate: number;
  /** ISO date of the latest observation. */
  latestDate: string;
  source: DataSource;
  warnings: string[];
}

const SOFR_URL = '/data/sofr.json';

type Fetcher = typeof globalThis.fetch;

/** Validate an array of raw SOFR rows, dropping any that fail (§8.2). Sorted ascending. */
function parseSofrRows(raw: unknown): SofrObservation[] {
  if (!Array.isArray(raw)) return [];
  const valid: SofrObservation[] = [];
  for (const row of raw) {
    const parsed = sofrObservationSchema.safeParse(row);
    if (parsed.success) valid.push(parsed.data);
  }
  valid.sort((a, b) => a.date.localeCompare(b.date));
  return valid;
}

function buildSofrResult(
  observations: SofrObservation[],
  source: DataSource,
  warnings: string[],
): SofrLoadResult {
  const latest = observations[observations.length - 1];
  return {
    observations,
    latestRate: latest ? latest.rate : 0,
    latestDate: latest ? latest.date : '',
    source,
    warnings,
  };
}

/** Load SOFR history; fall back to the bundled copy on any fetch/parse failure. */
export async function loadSofr(fetchImpl: Fetcher = globalThis.fetch): Promise<SofrLoadResult> {
  const warnings: string[] = [];
  try {
    const res = await fetchImpl(SOFR_URL, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: unknown = await res.json();
    const observations = parseSofrRows(json);
    if (observations.length > 0) {
      return buildSofrResult(observations, 'remote', warnings);
    }
    warnings.push('SOFR data was empty or invalid; using the bundled copy.');
  } catch (err) {
    warnings.push(`Could not load live SOFR data (${describeError(err)}); using the bundled copy.`);
  }
  const fallback = parseSofrRows(fallbackSofr);
  return buildSofrResult(fallback, 'bundled-fallback', warnings);
}

function describeError(err: unknown): string {
  return err instanceof Error ? err.message : 'unknown error';
}
